import React from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import { withStyles } from "@material-ui/core/styles"
import CardMedia from "@material-ui/core/CardMedia"
import Grid from "@material-ui/core/Grid"
import Paper from "@material-ui/core/Paper"
import Typography from "@material-ui/core/Typography"
import IconButton from "@material-ui/core/IconButton"
import GitHubIcon from "@material-ui/icons/GitHub"
import EmailIcon from "@material-ui/icons/Email"
import WebIcon from "@material-ui/icons/Language"
import Tooltip from "@material-ui/core/Tooltip"

import teamlist from "../../../content/people/people.yml"

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(8),
  },
  paper: {
    height: "100%",
  },
  details: {
    margin: theme.spacing(1, 1, 1, 0),
  },
  cover: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    margin: theme.spacing(2),
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: theme.palette.background.default,
  },
  memoriamCover: {
    filter: "grayscale(100%)",
  },
  icon: {
    fontSize: 18,
    padding: theme.spacing(1),
  },
  container: {
    margin: theme.spacing(2, 0, 4),
  },
})

const default_bio_faculty = `
I am currently a faculty member in School of Computing, NUS
`

const default_bio_res_fellow = `
I am currently a Research Fellow in the Database Systems Research Group 
at NUS, working with Professor Beng Chin Ooi.
`

const default_bio_phd = `
I am currently a Ph.D candidate in School of Computing, NUS.
`

const default_bio_res_asst = `
I am currently a Research Assistant in School of Computing, NUS.
`

const default_bio_intern = `
I am currently an intern in NUS School of Computing.
`

const default_bio_memoriam = `
In loving memory of a valued member of our community.
`

function Group(props) {
  const { avatarList, title, description, classes, members } = props
  let bioText = ""

  switch (title) {
    case "Faculty":
      bioText = default_bio_faculty
      break
    case "Research Fellow":
      bioText = default_bio_res_fellow
      break
    case "PhD Student":
      bioText = default_bio_phd
      break
    case "Research Assistant":
      bioText = default_bio_res_asst
      break
    case "Intern":
      bioText = default_bio_intern
      break
    case "Memoriam":
      bioText = default_bio_memoriam
      break
    default:
      bioText = "NA"
  }

  const findMatchingProfile = (pic_filename, allPicFiles = avatarList) => {
    const matchedNode = allPicFiles.find(
      ({ node }) => node.childImageSharp.fixed.originalName === pic_filename
    )

    return matchedNode.node.childImageSharp.fixed.src
  }

  return (
    members &&
    members?.length && (
      <div>
        <Typography gutterBottom component="h2" variant="h5">
          {title}
        </Typography>

        <Typography>{description}</Typography>

        <Grid container spacing={2} className={classes.container}>
          {members.map((member) => (
            <Grid key={member.pic_filename} item xs={12} md={6}>
              <Paper className={classes.paper}>
                <Grid container wrap="nowrap">
                  <Grid item>
                    <CardMedia
                      className={`${classes.cover} ${title === "Memoriam" ? classes.memoriamCover : ""}`}
                      image={findMatchingProfile(member.pic_filename)}
                      title={member.name}
                    />
                  </Grid>

                  <Grid item>
                    <div className={classes.details}>
                      <Typography component="h3" variant="h6">
                        {member.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        {member.bio ? member.bio : bioText}
                      </Typography>

                      <Grid container>
                        {member.email && (
                          <a
                            href={`mailto:${member.email}?Subject=NUS-DB-System-Website`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Tooltip title="Email">
                              <IconButton
                                aria-label="email"
                                className={classes.icon}
                              >
                                <EmailIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </a>
                        )}

                        {member.github && (
                          <a
                            href={`https://github.com/${member.github}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Tooltip title="GitHub">
                              <IconButton
                                aria-label="github"
                                className={classes.icon}
                              >
                                <GitHubIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </a>
                        )}

                        {member.website && (
                          <a
                            href={`${member.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Tooltip title="Website">
                              <IconButton
                                aria-label="website"
                                className={classes.icon}
                              >
                                <WebIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </a>
                        )}
                      </Grid>
                    </div>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </div>
    )
  )
}

Group.propTypes = {
  avatarList: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  description: PropTypes.string.isRequired,
  members: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
}

function Team(props) {
  const { classes } = props
  const data = useStaticQuery(graphql`
    query HomePagePeopleQuery {
      allFile(
        filter: { absolutePath: { regex: "/content/images/dbsystem-people/" } }
      ) {
        edges {
          node {
            childImageSharp {
              fixed(height: 200) {
                originalName
                src
              }
            }
          }
        }
      }
    }
  `)

  return (
    <div className={classes.root}>
      <Group
        title="Memoriam"
        description={`In remembrance of NUS Database System research group's  inspiring leader and mentor.`}
        members={teamlist.memoriam}
        avatarList={data.allFile.edges}
        {...props}
      />
      <Group
        title="Faculty"
        description={`NUS Database System research group is guided by several NUS School of Computing faculty members.`}
        members={teamlist.faculty}
        avatarList={data.allFile.edges}
        {...props}
      />
      <Group
        title="Research Fellow"
        description={`Research Fellows and Senior Research Fellows from NUS School of Computing`}
        members={teamlist.research_fellow}
        avatarList={data.allFile.edges}
        {...props}
      />
      <Group
        title="PhD Student"
        description={`PhD students supervised by DB System group`}
        members={teamlist.phd}
        avatarList={data.allFile.edges}
        {...props}
      />
      <Group
        title="Research Assistant"
        description={`Research Assistants in NUS DB System group`}
        members={teamlist.research_assistant}
        avatarList={data.allFile.edges}
        {...props}
      />
      <Group
        title="Intern"
        description={`Interns and visiting students and researchers`}
        members={teamlist.intern}
        avatarList={data.allFile.edges}
        {...props}
      />
    </div>
  )
}

export default withStyles(styles)(Team)
